import { useEffect } from 'react'

/**
 * Locks document-level scrolling while a full-height "app shell" page is mounted (editor, reader).
 *
 * Why this exists: those pages render a `100dvh` shell with a pinned header/toolbar at the top and
 * an inner `overflow: auto` content scroller. But the document body stays scrollable by default, so
 * on mobile it can drift — user scroll/rubber-band, or programmatic `scrollIntoView` (the editor's
 * mount focus + Lexical's selection) — dragging the whole top region up behind the address bar and
 * trimming a few pixels off the header.
 *
 * `overflow: hidden` is not enough on its own: it blocks user scrolling but NOT programmatic
 * scrolling, so a body that's taller than the viewport can still be nudged. The robust lock is
 * `position: fixed` on the body (the standard scroll-lock technique): a fixed body is pinned to the
 * viewport with no scrollable overflow, so neither the user nor `scrollIntoView` can move it. The
 * inner content container becomes the only scrollable surface. Previous inline styles are restored
 * on unmount, so document-flow pages (login, dashboard, …) are unaffected.
 */
export function useBodyScrollLock(): void {
  useEffect(() => {
    const html = document.documentElement
    const { body } = document

    const previous = {
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyBottom: body.style.bottom,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
    }

    html.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'none'
    body.style.position = 'fixed'
    body.style.top = '0'
    body.style.left = '0'
    body.style.right = '0'
    body.style.bottom = '0'
    body.style.width = '100%'
    body.style.overflow = 'hidden'
    body.style.overscrollBehavior = 'none'

    return () => {
      html.style.overflow = previous.htmlOverflow
      html.style.overscrollBehavior = previous.htmlOverscroll
      body.style.position = previous.bodyPosition
      body.style.top = previous.bodyTop
      body.style.left = previous.bodyLeft
      body.style.right = previous.bodyRight
      body.style.bottom = previous.bodyBottom
      body.style.width = previous.bodyWidth
      body.style.overflow = previous.bodyOverflow
      body.style.overscrollBehavior = previous.bodyOverscroll
    }
  }, [])
}
